import { ArrowRightIcon, ArrowUpRightIcon } from '@langchain/untitled-ui-icons';

import { useEffect, useState } from 'react';

import GraphFillIcon from '@/Pages/Graph/icons/GraphFillIcon.svg?react';
import { Button, GroupedTabs, Text } from '@/design-system';
import { useUpdateOnboardingState } from '@/hooks/useSwr';
import OpenAIIcon from '@/icons/OpenAILogo.svg?react';
import VercelIcon from '@/icons/VercelLogo.svg?react';
import { createName } from '@/utils/create-name';
import { capitalize } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { useObservabilityOnboardingSessionAndRuns } from '../hooks/useObservabilityOnboardingSessionAndRuns';
import { TracingNextSteps } from './TracingNextSteps';
import EnvQuickstart from './TracingQuickstart';
import { Framework } from './constants';

const Framework_TO_ICON = {
  [Framework.LangGraph]: <GraphFillIcon className="h-5 w-5 shrink-0" />,
  [Framework.OpenAI]: <OpenAIIcon className="h-5 w-5 shrink-0" />,
  [Framework.Vercel]: <VercelIcon className="h-5 w-5 shrink-0" />,
};
const createProjectName = createName.bind(null, 'pr');

const tabs = [
  {
    display: 'Python',
    value: 'python',
  },
  {
    display: 'TypeScript',
    value: 'typescript',
  },
];

export function SetupObservability(props: {
  hideTraceAnApplicationText?: boolean;
  className?: string;
  onNext?: () => void;
}) {
  const [projectName, setProjectName] = useState(createProjectName);

  const [framework, setFramework] = useState<Framework>(Framework.LangGraph);

  const [lang, setLang] = useState<'python' | 'typescript'>('python');

  const {
    hasSession,
    sessionId,
    run,
    rootRuns,
    childRunLookup,
    hasModifiedSystemMessage,
    hasModifiedTools,
    isLoadingSystemMessageQuery,
    isLoadingToolsQuery,
  } = useObservabilityOnboardingSessionAndRuns({
    projectName,
    lang,
  });

  const { trigger: updateOnboardingState } = useUpdateOnboardingState(
    'tracing_completed_at'
  );

  useEffect(() => {
    if (hasSession) {
      updateOnboardingState({});
    }
  }, [hasSession, updateOnboardingState]);

  if (hasSession) {
    return (
      <TracingNextSteps
        projectName={projectName}
        lang={lang}
        run={run}
        sessionId={sessionId}
        rootRuns={rootRuns}
        childRunLookup={childRunLookup}
        hasModifiedSystemMessage={hasModifiedSystemMessage}
        hasModifiedTools={hasModifiedTools}
        isLoadingSystemMessageQuery={isLoadingSystemMessageQuery}
        isLoadingToolsQuery={isLoadingToolsQuery}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex h-screen items-start gap-[54px] p-[72px]',
        props.className
      )}
    >
      <div className="flex h-full flex-col items-start gap-5">
        <Text variant="h4" color="secondary">
          Pick a framework
        </Text>
        <div className="flex flex-col items-start gap-3">
          {Object.values(Framework).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFramework(f);
                if (f === Framework.OpenAI) {
                  setLang('python');
                }
                if (f === Framework.Vercel) {
                  setLang('typescript');
                }
              }}
              type="button"
              className={cn(
                'inline-flex items-center gap-2 whitespace-nowrap',
                'rounded-lg border border-primary px-9 py-6',
                framework === f &&
                  'border border-brand-strong bg-brand-tertiary'
              )}
            >
              {Framework_TO_ICON[f]}
              <Text
                variant="h3"
                weight="medium"
                color="primary"
                className="inline"
              >
                {f}
              </Text>
            </button>
          ))}
        </div>
        <a
          href="https://docs.langchain.com/langsmith/trace-with-langchain"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4"
        >
          <Button
            color="secondary"
            type="button"
            variant="underlined"
            size="md"
            rightDecorator={ArrowUpRightIcon}
          >
            Explore more integrations
          </Button>
        </a>
        {props.onNext && (
          <div className="mt-auto">
            <Button
              color="secondary"
              type="button"
              variant="underlined"
              onClick={props.onNext}
              size="md"
              rightDecorator={ArrowRightIcon}
            >
              Skip
            </Button>
          </div>
        )}
      </div>
      <div className="flex h-full grow flex-col items-start gap-4">
        {!props.hideTraceAnApplicationText && (
          <Text variant="h4" color="secondary">
            Trace an application
          </Text>
        )}
        <div className="flex w-full items-center justify-between gap-2">
          <Text variant="h1" className="text-5xl" weight="normal">
            Build an agent
          </Text>
          {framework === Framework.LangGraph ? (
            <GroupedTabs
              value={lang}
              onChange={(value) => setLang(value as 'python' | 'typescript')}
              options={tabs}
            />
          ) : (
            <Text> {capitalize(lang)} only</Text>
          )}
        </div>
        <Text variant="md" color="quaternary">
          Build a ReAct-style agent, with two components: a tool call for
          weather related questions and an LLM call for general queries
        </Text>
        <div
          className={cn(
            'w-full overflow-y-auto rounded-3xl border border-secondary bg-primary text-left',
            'shadow-lg'
          )}
        >
          <EnvQuickstart
            framework={framework}
            lang={lang}
            projectName={projectName}
            setProjectName={setProjectName}
          />
        </div>
      </div>
    </div>
  );
}
